import { useAddChallengeToPatient } from '_api/mutations';
import { AddChallengeToPatientPayload, Challenge } from '_api/types';
import { SEVERITY } from '_constants/derived';
import DefaultChallengeToPatientSettings from '_constants/object-templates/DefaultChallengeToPatientSettings';
import { AlertMessage, BaseModal } from 'components/molecules';
import { AddChallengeToPatientForm } from 'components/organisms/forms';
import { normalizePatientInstructions } from 'components/organisms/forms/patientInstructions';
import React, { useState, useEffect } from 'react';

import { buildChallengePayload } from './buildChallengePayload';
import { AddToPatientModalProps } from './types';

export const AddChallengeToPatientModal = ({
  content,
  open,
  onClose,
  id,
}: AddToPatientModalProps<Challenge>) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { mutateAsync: addChallengeToPatient } = useAddChallengeToPatient();
  const [payload, setPayload] = useState<AddChallengeToPatientPayload>(
    DefaultChallengeToPatientSettings,
  );

  useEffect(() => {
    if (open) {
      setShowSuccess(false);
      setErrorMessage('');
      setPayload(buildChallengePayload(id, 0, content));
    }
  }, [content, id, open]);

  const close = () => {
    onClose();
    setPayload(DefaultChallengeToPatientSettings);
  };

  const formatErrorMessage = (errorMessage: string) => {
    const regex =
      /patient with id '\d+' already has content type '(\w+)' for content with id '\d+'/i;
    const match = errorMessage.match(regex);
    if (match) {
      const contentType = match[1];
      if (content.title) {
        return `Patient already has ${contentType.toLowerCase()} '${content.title}'`;
      }
    }
    return errorMessage;
  };

  const onUpdate = async (key: any, arg: any) => {
    setPayload((current) => ({
      ...current,
      contentId: id,
      [key]: arg,
    }));
  };

  const onSubmit = () => {
    addChallengeToPatient([
      {
        ...payload,
        instructionList: normalizePatientInstructions(payload.instructionList),
        reps: 1,
        sets: 1,
        perDay: 1,
        regimenType: 'HABIT',
      },
    ] as AddChallengeToPatientPayload[])
      .then(() => {
        setShowSuccess(true);
        setTimeout(() => onClose(), 1500);
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          setErrorMessage(
            'Unexpected error occured. Please close the modal and try again.',
          );
        } else if (error.message) {
          setErrorMessage(formatErrorMessage(error.message));
        } else {
          setErrorMessage('Unexpected error. Please Try again.');
        }
      });
  };

  return (
    content && (
      <BaseModal open={open} onClose={close} title={'Add To A Patient'}>
        <AddChallengeToPatientForm
          initValue={payload}
          onBack={close}
          onSubmit={onSubmit}
          onUpdate={onUpdate}
        />
        <AlertMessage
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          message={`${content.title} successfully added to patient`}
        />
        <AlertMessage
          open={!!errorMessage}
          onClose={() => setErrorMessage('')}
          severity={SEVERITY.error}
          message={errorMessage}
        />
      </BaseModal>
    )
  );
};
